import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { keyName } from '@bingads-webui/keycode';
import { withLiveRegionProp } from '@bingads-webui-react/hoc-with-live-region';
/**
 * SearchBox component provides an input field for searching through content
 */

export class SearchBox extends PureComponent {
  static defaultProps = {
    maxLength: 150,
    ariaLabel: '',
    placeholder: '',
    isDisabled: false,
    iconClasses: [],
    searchBoxClasses: [],
    onFocus: () => {},
    onBlur: () => {},
  };

  onKeyPress = (event) => {
    // enter
    if (event.key === keyName[13]) {
      event.preventDefault();
      this.props.onSearch(this.props.value);
    }

    // escape
    if (event.key === keyName[0x1B]) {
      event.preventDefault();
      this.props.onSearch('');
    }
  }

  onClick = (event) => {
    event.preventDefault();
    this.props.onSearch(this.props.value);
  }

  onChange = (event) => {
    this.props.onQueryChange(event.target.value);
  }

  onFocusCapture = (event) => {
    this.props.onFocus(event.target.value);
  }

  onBlur = (event) => {
    this.props.onBlur(event.target.value);
  }

  render() {
    return (
      <div className={`ba-search-box ${this.props.searchBoxClasses.join(' ')}`}>
        <div className="search-box__input-group">
          <input
            className="search-box__input form-control"
            type="text"
            maxLength={this.props.maxLength}
            placeholder={this.props.placeholder}
            onKeyPress={this.onKeyPress}
            value={this.props.value}
            onChange={this.onChange}
            aria-label={this.props.ariaLabel || this.props.placeholder}
            disabled={this.props.isDisabled}
            onFocusCapture={this.onFocusCapture}
            onBlur={this.onBlur}
          />

          <span
            className={`search-box__icon ${this.props.iconClasses.join(' ')}`}
            tabIndex="0"
            role="button"
            onClick={this.onClick}
            onKeyPress={this.onClick}
            aria-label={this.props.ariaLabel || this.props.placeholder}
          />
        </div>
      </div>
    );
  }
}

SearchBox.propTypes = {
  /**
   * the maximum length of the search text
   */
  maxLength: PropTypes.number,
  /**
   * Gets called when user clicks search icon and preses 'Enter' key.
   * @param {string} - input query
   */
  onSearch: PropTypes.func.isRequired,
  /**
   * Gets called when user inputs
   * @param {string} - input query
   */
  onQueryChange: PropTypes.func.isRequired,
  /**
   * the aria-label for the search box
   */
  ariaLabel: PropTypes.string,
  /**
   * the placeholder for the search box
   */
  placeholder: PropTypes.string,
  /**
   * disable the input box or not
   */
  isDisabled: PropTypes.bool,
  /**
   * input box value
   */
  value: PropTypes.string.isRequired,
  /**
   * the classes to assign to the icon's
   * span element. This is helpful if glyphicon is to be used. Then two classes must be
   * passed: class of the icon, and general glyphicon class.
   */
  iconClasses: PropTypes.arrayOf(PropTypes.string),
  /**
   * the classed to assign the overall style to the component
   */
  searchBoxClasses: PropTypes.arrayOf(PropTypes.string),
  /**
   * Get called when focused
   */
  onFocus: PropTypes.func,
  /**
   * Get called when lose focus
   */
  onBlur: PropTypes.func,
};

export const SearchBoxWithLiveResults = withLiveRegionProp(SearchBox);

export const getSearchResultLiveRegionText = (totalCount, emptyText = '', resultText = '') =>
  (totalCount === 0 ? emptyText : resultText);

export const SearchResultLiveRegion = (props) => {
  const {
    totalCount, 'aria-live': ariaLive, emptyText, resultText, role,
  } = props;
  const liveRegionText = getSearchResultLiveRegionText(totalCount, emptyText, resultText);

  return (<div
    className="search-result live-region"
    aria-live={ariaLive}
    aria-label={liveRegionText}
    role={role}
  />);
};

SearchResultLiveRegion.propTypes = {
  totalCount: PropTypes.number.isRequired,
  'aria-live': PropTypes.oneOf(['off', 'polite', 'assertive']),
  emptyText: PropTypes.string,
  resultText: PropTypes.string,
  role: PropTypes.string,
};

SearchResultLiveRegion.defaultProps = {
  'aria-live': 'assertive',
  emptyText: '',
  resultText: '',
  role: 'region',
};
